// Arma el link público del catálogo y copia texto al portapapeles con respaldos.
import { getCodigoPais } from '../services/negocioActual';

// Link del catálogo. `prefijos` limita las categorías visibles (opcional);
// `cat` + `talla` abren el catálogo pre-filtrado a una categoría/talla.
// `origen` (?o=) dice por dónde se compartió: el pedido lo guarda (sql/104).
export function linkCatalogo({ negocioId, slug = null, prefijos = [], cat = null, tipo = null, talla = null, marca = null, ofertas = false, origen = null }) {
  const f = new URLSearchParams();
  if (prefijos.length) f.set('cats', prefijos.join(','));
  if (cat) f.set('cat', cat);
  if (tipo) f.set('tipo', tipo);
  if (talla) f.set('talla', talla);
  if (marca) f.set('marca', marca);
  if (ofertas) f.set('ofertas', '1'); // 🏷️ solo lo que está en oferta
  if (origen) f.set('o', origen);
  // Link para COMPARTIR: pasa por /c (tarjeta de vista previa con nombre de la
  // tienda + foto) y redirige al catálogo. Con nombre corto: /c/lolitas
  if (slug) {
    const qs = f.toString();
    return `${window.location.origin}/c/${slug}${qs ? '?' + qs : ''}`;
  }
  if (negocioId) f.set('n', negocioId);
  return `${window.location.origin}/c?${f.toString()}`;
}

// Link público de UN solo producto (para abrir esa visualización en el catálogo).
export function linkProducto({ negocioId, productoId }) {
  const params = new URLSearchParams();
  if (negocioId) params.set('n', negocioId);
  params.set('producto', productoId);
  return `${window.location.origin}/catalogo?${params.toString()}`;
}

// Link para COMPARTIR un producto (WhatsApp/Facebook): pasa por /p, un endpoint
// del servidor que arma la TARJETA con foto/nombre/precio (Open Graph) y luego
// redirige al catálogo. Así la vista previa muestra la imagen del producto.
// Página de UN pedido del catálogo con todas sus prendas (sql/120). Su vista
// previa en WhatsApp es un collage con las fotos de todas.
export const linkPedido = (grupo) => `${window.location.origin}/pd/${grupo}`;

export function linkProductoOG({ negocioId, productoId, origen = null }) {
  const params = new URLSearchParams();
  if (negocioId) params.set('n', negocioId);
  params.set('producto', productoId);
  // v: cambia la dirección cuando cambia la tarjeta, así WhatsApp no usa una vista previa vieja.
  params.set('v', '2');
  if (origen) params.set('o', origen);
  return `${window.location.origin}/p?${params.toString()}`;
}

export function compartirWhatsApp(url, texto = '') {
  const msg = texto ? `${texto}\n${url}` : url;
  // api.whatsapp.com/send preserva los emojis (wa.me a veces los rompe a "�").
  window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
}

// Número para wa.me, multi-país:
//  • si trae "+" (ej. +1, +52, +504) → es internacional, se usa tal cual;
//  • si ya tiene 11+ dígitos → se asume que ya trae código de país;
//  • si es un número LOCAL corto → se antepone el código del país del negocio.
export function numeroWhatsApp(telefono, codigoPais) {
  const raw = (telefono || '').trim();
  const digitos = raw.replace(/\D/g, '');
  if (raw.startsWith('+')) return digitos;
  if (digitos.length >= 11) return digitos;
  const code = String(codigoPais != null ? codigoPais : getCodigoPais()).replace(/\D/g, '');
  return code ? code + digitos : digitos;
}

// Link de WhatsApp a un número específico con un texto. Se usa
// api.whatsapp.com/send (en vez de wa.me) porque preserva bien los emojis.
export function linkWhatsAppA(telefono, texto) {
  return `https://api.whatsapp.com/send?phone=${numeroWhatsApp(telefono)}&text=${encodeURIComponent(texto)}`;
}

// Link para abrir los MENSAJES (DM) de Instagram con la tienda.
// ig.me/m/<usuario> es el enlace oficial de Instagram para "enviar mensaje":
// abre la conversación con esa cuenta directamente en la app de Instagram.
// Acepta el usuario con o sin @, o pegado como URL de perfil.
export function linkInstagramDM(usuario) {
  const u = (usuario || '')
    .trim()
    .replace(/^@/, '')
    .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '')
    .replace(/\/+$/, '')
    .trim();
  return u ? `https://ig.me/m/${encodeURIComponent(u)}` : null;
}

export function compartirFacebook(url) {
  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'noopener,noreferrer');
}

// Instagram no tiene un link web para "enviar a una persona". La forma
// correcta es el menú de compartir del teléfono (Web Share), donde aparece
// Instagram y al elegirlo te lleva a seleccionar la persona (DM) o la historia.
// En PC (sin ese menú) copiamos el link y abrimos Instagram.
export async function compartirInstagram(url, texto = '', avisar) {
  if (navigator.share) {
    try {
      await navigator.share({ title: texto || 'Producto', text: texto || '', url });
      return;
    } catch (e) {
      if (e && e.name === 'AbortError') return; // el usuario cerró el menú
      // cualquier otro error → seguimos al respaldo
    }
  }
  const ok = await copiarTexto(url);
  window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
  if (avisar) avisar(ok
    ? 'Link copiado 📋 Pégalo en tu historia o mensaje de Instagram'
    : 'Copia el link y pégalo en Instagram', 'info', 6000);
}

// Copia con 3 niveles de respaldo. Devuelve true si copió al portapapeles.
export async function copiarTexto(texto) {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    /* sigue al respaldo */
  }
  try {
    const campo = document.createElement('textarea');
    campo.value = texto;
    campo.style.position = 'fixed';
    campo.style.opacity = '0';
    document.body.appendChild(campo);
    campo.focus();
    campo.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(campo);
    return ok;
  } catch {
    return false;
  }
}
